/**
 * The builders verify every value that has an id behind it.
 *
 * The owner's standing rule (tags decision d21, 2026-09-29; knowledge/rules/
 * product-principles.md; docs/plans/tags.md §3.7 "The builder verifies" and
 * §5.2): a value typed in the filter builder or the condition builder that
 * stands for a record — a choice, an item, a user, a tag — is checked where it
 * was typed. An unknown or ambiguous value is flagged there, and the matches
 * are offered as one-click replacements.
 *
 * THE CHECK IS ADVISORY. It never holds Apply or Save: the API decides what a
 * query means, and it accepts every one of these (an unknown choice compiles
 * to a text match that finds nothing, an unknown item id matches nothing, a
 * typed name on a user element contributes nothing — keepr-api utils/kql/
 * compile.js and compileValues.js). A flag says "this will probably find
 * nothing, did you mean…"; it is never a refusal.
 *
 * Pure: no React, no antd, no network. A component hands in the vocabulary it
 * already holds (a choice list rides on the field; a lookup's items come from
 * the picker's own fetch; a user element's collaborators from
 * `useCollaborators`), and gets back a verdict it can render.
 *
 * ── The kinds ──────────────────────────────────────────────────────────────
 * A small registry (`registerIdValueKind`). Each kind says which fields it
 * covers and how it checks one typed value. Three are registered here:
 *
 *   choice  a `choice` element with a choice list (the platform's
 *           rendersAsChoice rule, utils/choiceValues — never a short text),
 *           and the system choice fields the list subjects build the same
 *           way (`set`, `collection`, a card's `parent`, …). The API compares
 *           a choice's STORED VALUE, case-insensitively, and never its label.
 *   lookup  an item lookup, and a facet (which wears `card-lookup`). A 24-hex
 *           id is the item; TEXT IS A TITLE MATCH BY DESIGN (compile.js
 *           compileLookupField resolves it through the target card's title
 *           element), so text is never flagged: it is reported as
 *           'title-match', with the items that have that title offered.
 *   user    a user element. The API matches an account id or `me`, and a
 *           typed name contributes nothing — so text is flagged.
 *
 *   tag     the `tags` system field (T4b; registered below with `before:
 *           'choice'`). Its vocabulary rides on the field (utils/kqlBuilder
 *           tagsFilterField): a 24-hex id is the tag; a name, an alias or a
 *           path resolves among the collection's tags (and, in a live
 *           query, my private tags) — one match is `via: 'label'` (the
 *           builder stores the id, as it stores a choice's value for its
 *           label), several are 'ambiguous' with their paths offered; an
 *           ITEM tag resolves by id only (ruled d13), so its title typed is
 *           flagged with the item tag offered; a private tag named where the
 *           query is shared (`mode: 'collection'`) is flagged and says why.
 *           Where the query decides who sees what (`mode: 'access'`, T7) only
 *           a RESTRICTED collection tag resolves — a name among the restricted
 *           ones — and an unrestricted, private or item tag is flagged with
 *           "Only a restricted tag can decide who sees what" (checkAccessTag).
 *
 * An OPERATOR the API refuses (`tags ~ x`, ranges on tags, emptiness in an
 * access query) is not a value's check: `checkOperator` / `checkQueryOperators`
 * say it in the same shape, and it is not advisory.
 *
 * ── The verdict ────────────────────────────────────────────────────────────
 *   { kind, status, typed, value?, via?, matches, message, matchesLead? }
 *
 *   status   'ok' | 'unknown' | 'ambiguous' | 'title-match'
 *   value    'ok' only: the stored spelling the typed text stands for
 *   via      'ok' only: how it matched — 'value' (the stored value itself,
 *            any case), 'keyword' (`this`, a word the field's API reads beside
 *            its values), or 'label' (the name a person reads; the API would
 *            NOT match it as typed, so a builder writes `value` in its place —
 *            see resolveQueryLabels)
 *   matches  [{ value, label, detail? }] — the offered replacements, at most
 *            MAX_MATCHES
 *   message  one plain sentence for the flag; '' for a plain 'ok'
 *
 * `null` means there is nothing to say: the field has no id behind it, the
 * operator takes no value or a substring, the value is a template token or
 * the `me` keyword, or the vocabulary to judge by has not arrived yet.
 */
import { foldSearchText } from './strings';
import { ME_VALUE, userDisplayName } from './userElements';
import { normalizeId } from './ids';
import { rendersAsChoice } from './choiceValues';
import { entryColumnFields, findField, isEntriesField, isNullaryOperator, normalizeFacetAliases, refusedOperatorMessage } from './kqlBuilder';
import { isBlock, parse, serialize } from './kql';
import { isPrivateTag, isRestrictedTag, tagIdOf, tagPathLabel } from './tags';
import { tagsNamed } from './tagNames';
import { itemTagTitleFor, rememberedItemTagTitles } from './tagTitleMemory';

export const ID_CHECK_STATUS = Object.freeze({
	OK: 'ok',
	UNKNOWN: 'unknown',
	AMBIGUOUS: 'ambiguous',
	TITLE_MATCH: 'title-match',
});

/** How many replacements a flag offers at most. */
export const MAX_MATCHES = 5;

const HEX_ID = /^[0-9a-f]{24}$/i;

/** Does this text spell a record id (24 hex digits)? */
export const looksLikeId = value => HEX_ID.test(String(value ?? '').trim());

// An automation template token (`"{{trigger.id}}"`): the API substitutes it
// before the condition compiles, so it is never a value somebody mistyped.
const TEMPLATE_TOKEN = /\{\{[^{}]*\}\}/;

// Comparisons whose value is a whole value, not a substring or a bound. Only
// these can name a record.
const IDENTITY_CMPS = new Set(['eq', 'ne', 'in', 'nin']);
const SUBSTRING_CMPS = new Set(['contains', 'ncontains']);

const fold = text => foldSearchText(text).trim();

// ------------------------------------------------------------------ registry

const kinds = [];

/**
 * Register (or replace) a kind of id-backed value.
 *
 *   appliesTo(field) → boolean   which field descriptors it covers
 *   check(text, { field, cmp, vocabulary }) → verdict | null
 *
 * Kinds are asked in order and the first that applies wins; `before: '<kind>'`
 * places a new one ahead of an existing kind (a tag field must be asked before
 * a generic choice rule could claim it).
 */
export function registerIdValueKind(name, { appliesTo, check, before } = {}) {
	if (!name || typeof appliesTo !== 'function' || typeof check !== 'function') {
		throw new Error('registerIdValueKind needs a name, appliesTo(field) and check(text, context).');
	}
	const entry = { name, appliesTo, check };
	const existing = kinds.findIndex(kind => kind.name === name);
	if (existing >= 0) kinds.splice(existing, 1);
	const at = before ? kinds.findIndex(kind => kind.name === before) : -1;
	if (at >= 0) kinds.splice(at, 0, entry);
	else kinds.push(entry);
}

/** The registered kinds' names, in the order they are asked. */
export const idValueKinds = () => kinds.map(kind => kind.name);

const kindOf = field => kinds.find(kind => kind.appliesTo(field)) || null;

/** The kind of id-backed value a field holds, or null when it holds none. */
export const idValueKindFor = field => (field ? kindOf(field)?.name || null : null);

// ------------------------------------------------------------------ verdicts

const verdict = (kind, status, typed, extra = {}) => ({
	kind,
	status,
	typed,
	matches: [],
	message: '',
	...extra,
});

/**
 * Check one typed value against its field. `cmp` is the row's operator
 * (default `eq`); `vocabulary` is what the component holds for this kind
 * (see each kind). Returns a verdict, or null when there is nothing to say.
 */
export function checkIdValue(field, typed, { cmp = 'eq', vocabulary } = {}) {
	if (!field || field.unknown) return null;
	if (isNullaryOperator(cmp)) return null;
	const kind = kindOf(field);
	if (!kind) return null;
	if (typed === undefined || typed === null || typeof typed === 'object' || typeof typed === 'boolean') return null;
	const text = String(typed).trim();
	if (!text || text === ME_VALUE || TEMPLATE_TOKEN.test(text)) return null;
	const result = kind.check(text, { field, cmp, vocabulary });
	return result ? { ...result, kind: kind.name } : null;
}

/** Does this verdict call for the flag (the warning state)? */
export const isFlagged = result => !!result
	&& (result.status === ID_CHECK_STATUS.UNKNOWN || result.status === ID_CHECK_STATUS.AMBIGUOUS);

/**
 * The verdict a surface should SHOW, or null. What differs between surfaces is
 * a LABEL — a name that stands for exactly one stored value. Both builders
 * write the stored value in its place (resolveQueryLabels; for the condition
 * builder, ruled b01 on 2026-09-30: it keeps only ids and choice values).
 * `labels` says how the surface shows that:
 *   'resolve'  the surface holds the stored value itself (the builders'
 *              rows), so a label needs no word;
 *   'note'     the text on screen is what the person typed (a text mode,
 *              which is never rewritten under the cursor): a quiet note says
 *              what will be stored — `action` names the act that stores it,
 *              'apply' (the filter builder's Apply) or 'save' (a rule's
 *              Save, the condition builder).
 */
export function displayedCheck(result, { labels = 'resolve', action = 'apply' } = {}) {
	if (!result) return null;
	if (result.status !== ID_CHECK_STATUS.OK) return result;
	if (result.via !== 'label') return null;
	// The word for what a label names: 'choice' here; a kind that resolves
	// names too (tags, T4b) says its own.
	const noun = result.noun || 'choice';
	if (labels === 'note') {
		const stores = action === 'save' ? 'Saving stores' : 'Apply saves';
		return { ...result, matches: [], message: `“${result.typed}” is a ${noun}’s name. ${stores} the ${noun} itself.` };
	}
	return null;
}

/**
 * Users as a `user` check's vocabulary: `[{ value, label, searchText }]`,
 * named the way the platform names them and found by name, username or
 * email (the collaborators `useCollaborators` returns).
 */
export function userVocabularyOf(users = []) {
	return (users || [])
		.map(user => {
			const value = normalizeId(user?._id);
			if (!value) return null;
			const label = userDisplayName(user) || user?.email || value;
			const searchText = [label, user?.username, user?.email].filter(Boolean).join(' ');
			return { value, label, searchText };
		})
		.filter(Boolean);
}

// -------------------------------------------------------------- close matches

/** Edit distance, capped: anything past `max` is `max + 1`. */
function distance(a, b, max) {
	if (Math.abs(a.length - b.length) > max) return max + 1;
	let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
	for (let i = 1; i <= a.length; i++) {
		const current = [i];
		let best = i;
		for (let j = 1; j <= b.length; j++) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1;
			current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
			best = Math.min(best, current[j]);
		}
		if (best > max) return max + 1;
		previous = current;
	}
	return previous[b.length];
}

/**
 * The entries a mistyped value most likely meant, best first: those that
 * start with it, then those that contain it (or that it contains), then near
 * misspellings. `texts(entry)` gives the spellings an entry answers to.
 */
function closeMatches(entries, needle, texts) {
	const scored = [];
	entries.forEach((entry, index) => {
		let score = null;
		texts(entry).map(fold).filter(Boolean).forEach(hay => {
			let next = null;
			if (hay.startsWith(needle)) next = 0;
			else if (hay.includes(needle)) next = 1;
			else if (needle.includes(hay)) next = 2;
			else {
				const max = Math.max(1, Math.floor(Math.min(hay.length, needle.length) / 3));
				const d = distance(hay, needle, max);
				if (d <= max) next = 2 + d;
			}
			if (next !== null && (score === null || next < score)) score = next;
		});
		if (score !== null) scored.push({ entry, score, index });
	});
	return scored
		.sort((a, b) => a.score - b.score || a.index - b.index)
		.map(({ entry }) => entry);
}

// -------------------------------------------------------------------- choice

const choicesOf = field => (Array.isArray(field?.options?.choices) ? field.options.choices : []);

/**
 * One choice as `{ value, label }`, strings throughout. A legacy bare-string
 * choice is its own value and label.
 */
const choiceEntry = choice => {
	if (choice === null || choice === undefined) return null;
	if (typeof choice !== 'object') {
		const text = String(choice);
		return text ? { value: text, label: text } : null;
	}
	const raw = choice.value ?? choice.label;
	if (raw === undefined || raw === null || raw === '') return null;
	const value = String(raw);
	return {
		value,
		label: choice.label === undefined || choice.label === null || choice.label === '' ? value : String(choice.label),
		// A reference field's record key (kqlBuilder referenceChoices).
		aliases: Array.isArray(choice.aliases) ? choice.aliases.map(String) : [],
	};
};

const asMatch = entry => ({ value: entry.value, label: entry.label });

registerIdValueKind('choice', {
	// The platform's rendersAsChoice rule (utils/choiceValues): a Choice with a
	// list. A short text, number or date that kept a list from an earlier life
	// as a Choice is still a text, number or date (the list is inert, KPE-033).
	appliesTo: field => rendersAsChoice(field),
	check(text, { field, cmp }) {
		// A substring is a question about text, not a pick from the list.
		if (!IDENTITY_CMPS.has(cmp)) return null;
		const entries = choicesOf(field).map(choiceEntry).filter(Boolean);
		if (!entries.length) return null;
		const needle = fold(text);

		// The stored value, in any case: the API's text equality is
		// case-insensitive, so `DONE` finds what `done` finds.
		const byValue = entries.find(entry => fold(entry.value) === needle);
		if (byValue) return verdict('choice', ID_CHECK_STATUS.OK, text, { value: byValue.value, via: 'value', matches: [asMatch(byValue)] });

		// A word the API reads for this field beside its values (`this`, the
		// scoping collection).
		const keywords = Array.isArray(field?.options?.keywords) ? field.options.keywords : [];
		if (keywords.some(word => fold(word) === needle)) return verdict('choice', ID_CHECK_STATUS.OK, text, { value: text, via: 'keyword' });

		// A REFERENCE field (`card`, `set`) lists its records by ID — what a
		// filter names them by since deterministic filters (kqlBuilder
		// referenceChoices). A record's KEY is how text written before F3
		// named it: the same record, read case-insensitively as the API reads
		// a key, and Apply writes the id (one record) or asks which (several).
		// An id the list does not hold is a record no longer here. Any other
		// word is no verdict: the API reads a reference by key or id, never by
		// NAME (keepr-api utils/kql/resolveNames.js resolveCard), so a card
		// whose name matches is not what the text means — and a key this
		// page holds no alias for (shared, or a card the API does not
		// consider here) is the server's to read (the F3 review's F1).
		if (field?.reference) {
			const noun = field.reference === 'set' ? 'element set' : 'card';
			const byAlias = entries.filter(entry => entry.aliases.some(alias => fold(alias) === needle));
			if (byAlias.length === 1) {
				return verdict('choice', ID_CHECK_STATUS.OK, text, { value: byAlias[0].value, via: 'label', noun, matches: [asMatch(byAlias[0])] });
			}
			if (byAlias.length > 1) {
				return verdict('choice', ID_CHECK_STATUS.AMBIGUOUS, text, {
					noun,
					matches: byAlias.slice(0, MAX_MATCHES).map(entry => ({ ...asMatch(entry), detail: entry.label })),
					message: `${byAlias.length} ${noun}s are called “${text}”. Pick one:`,
				});
			}
			if (looksLikeId(text)) {
				return verdict('choice', ID_CHECK_STATUS.UNKNOWN, text, {
					noun,
					message: field.reference === 'set'
						? 'This element set is not on any card here. It may have been removed.'
						: 'This card is not in this collection. It may have been removed.',
				});
			}
			return null;
		}
		// A record's ID on any other list: judged only against a list whose
		// values ARE ids.
		if (looksLikeId(text) && !entries.every(entry => looksLikeId(entry.value))) return null;

		// The name a person reads. It stands for exactly one stored value —
		// unless two choices share it, and then it is the person's pick.
		const byLabel = entries.filter(entry => fold(entry.label) === needle);
		if (byLabel.length === 1) {
			return verdict('choice', ID_CHECK_STATUS.OK, text, { value: byLabel[0].value, via: 'label', matches: [asMatch(byLabel[0])] });
		}
		if (byLabel.length > 1) {
			return verdict('choice', ID_CHECK_STATUS.AMBIGUOUS, text, {
				matches: byLabel.slice(0, MAX_MATCHES).map(entry => ({ ...asMatch(entry), detail: entry.value })),
				message: `${byLabel.length} choices are called “${text}”. Pick one:`,
			});
		}

		// Neither: offer what it most likely meant, or — on a short list with
		// nothing close — the whole list.
		let offered = closeMatches(entries, needle, entry => [entry.label, entry.value]);
		if (!offered.length && entries.length <= MAX_MATCHES) offered = entries;
		const matches = offered.slice(0, MAX_MATCHES).map(asMatch);
		return verdict('choice', ID_CHECK_STATUS.UNKNOWN, text, {
			matches,
			message: matches.length
				? `“${text}” is not one of the choices. Did you mean:`
				: `“${text}” is not one of the choices.`,
		});
	},
});

// -------------------------------------------------------------------- lookup

/**
 * An item lookup, or a facet. `vocabulary` is what the item picker holds:
 *
 *   { items: [{ value: <id>, label: <title>, ... }], complete: boolean }
 *
 * `complete` says every stored id has been asked about (the picker's list has
 * arrived and its `?ids=` resolve has ANSWERED — a request that failed proves
 * nothing, so it leaves this false). Without it, an id that is not among
 * `items` has no verdict yet.
 */
registerIdValueKind('lookup', {
	appliesTo: field => field?.dataType === 'card-lookup',
	check(text, { cmp, vocabulary }) {
		const items = Array.isArray(vocabulary?.items) ? vocabulary.items : [];
		if (looksLikeId(text)) {
			const wanted = text.toLowerCase();
			const hit = items.find(item => String(item?.value ?? '').toLowerCase() === wanted);
			if (hit) return verdict('lookup', ID_CHECK_STATUS.OK, text, { value: hit.value, via: 'value', matches: [hit] });
			if (!vocabulary?.complete) return null;
			return verdict('lookup', ID_CHECK_STATUS.UNKNOWN, text, {
				message: 'This item can’t be found. It may have been deleted, or you can’t read it.',
			});
		}
		// TEXT IS A TITLE MATCH, BY DESIGN — never an error. The API finds
		// every item whose title is this (or, for "contains", holds it).
		if (!IDENTITY_CMPS.has(cmp) && !SUBSTRING_CMPS.has(cmp)) return null;
		const substring = SUBSTRING_CMPS.has(cmp);
		const needle = fold(text);
		// Pinning one item is offered for "is" only: under "contains" the
		// text is the question.
		const hits = substring ? [] : items.filter(item => fold(item?.label) === needle);
		return verdict('lookup', ID_CHECK_STATUS.TITLE_MATCH, text, {
			matches: hits.slice(0, MAX_MATCHES),
			total: hits.length,
			message: substring
				? `Matches any item whose title contains “${text}”.`
				: `Matches any item titled “${text}”.`,
			matchesLead: hits.length > 1 ? 'Or pick one:' : 'Or pick the item:',
		});
	},
});

// ---------------------------------------------------------------------- user

/**
 * A user element. `vocabulary` is the people who could be picked — the
 * collection's collaborators — as `[{ value: <id>, label: <name>,
 * searchText }]`. An id is not judged against it (the list is not every
 * user), only typed text is: the API never resolves a name.
 */
registerIdValueKind('user', {
	appliesTo: field => field?.dataType === 'user',
	check(text, { cmp, vocabulary }) {
		if (!IDENTITY_CMPS.has(cmp)) return null;
		const people = Array.isArray(vocabulary) ? vocabulary : [];
		if (looksLikeId(text)) {
			const wanted = text.toLowerCase();
			const hit = people.find(person => String(person?.value ?? '').toLowerCase() === wanted);
			return hit ? verdict('user', ID_CHECK_STATUS.OK, text, { value: hit.value, via: 'value', matches: [hit] }) : null;
		}
		const needle = fold(text);
		const matches = people
			.filter(person => fold(person?.searchText || person?.label).includes(needle))
			.slice(0, MAX_MATCHES);
		return verdict('user', ID_CHECK_STATUS.UNKNOWN, text, {
			matches,
			message: matches.length
				? `“${text}” matches nothing: a user is picked, not typed. Did you mean:`
				: `“${text}” matches nothing: a user is picked, not typed.`,
		});
	},
});

// ----------------------------------------------------------------------- tag

const tagMatch = tag => ({
	value: tagIdOf(tag),
	label: tagPathLabel(tag),
	...(isPrivateTag(tag) ? { detail: 'Private tag' } : {}),
});

const PRIVATE_HERE = 'This is shared with other people, so a private tag can’t be used here.';

/** The one rule of an ACCESS query's tags (T7, keepr-api `tag_not_restricted`), in its words. */
export const ONLY_RESTRICTED = 'Only a restricted tag can decide who sees what.';

/**
 * A tag named in an ACCESS query (T7: an access level's filter, a dynamic
 * group's rule, a collection link's or a public link's filter). The API
 * resolves only a RESTRICTED collection tag of the collection or one above it
 * — a name among restricted tags, stored as the id — and refuses every other:
 * an unrestricted tag (`tag_not_restricted`), a private tag, an item tag, a
 * tag it can't find (`unknown_tag`). So each is flagged here, and says why.
 */
function checkAccessTag(text, { collectionTags, privateTags, itemTitles }) {
	const restricted = collectionTags.filter(isRestrictedTag);
	const notRestricted = tag => verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, {
		message: `“${tagPathLabel(tag)}” isn’t a restricted tag. ${ONLY_RESTRICTED}`,
	});
	const privateOne = tag => verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, {
		message: `“${tag?.name || text}” is your private tag. ${ONLY_RESTRICTED}`,
	});
	const itemOne = title => verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, {
		message: `“${title}” is an item used as a tag. ${ONLY_RESTRICTED}`,
	});
	if (looksLikeId(text)) {
		const id = text.toLowerCase();
		const known = collectionTags.find(tag => tagIdOf(tag) === id);
		if (known && isRestrictedTag(known)) return verdict('tag', ID_CHECK_STATUS.OK, text, { value: id, via: 'value', matches: [tagMatch(known)] });
		if (known) return notRestricted(known);
		const mine = privateTags.find(tag => tagIdOf(tag) === id);
		if (mine) return privateOne(mine);
		const item = itemTagTitleFor(id) || (itemTitles && itemTitles[id]);
		if (item?.title) return itemOne(item.title);
		return verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, {
			message: 'This tag can’t be found. It may have been deleted, or you can’t see it.',
		});
	}
	// A name resolves among the RESTRICTED tags only (the API's rewrite).
	const candidates = tagsNamed(text, restricted);
	if (candidates.length === 1) {
		return verdict('tag', ID_CHECK_STATUS.OK, text, {
			value: tagIdOf(candidates[0]),
			via: 'label',
			noun: 'tag',
			resolvesOnSave: true,
			matches: [tagMatch(candidates[0])],
		});
	}
	if (candidates.length > 1) {
		return verdict('tag', ID_CHECK_STATUS.AMBIGUOUS, text, {
			matches: candidates.slice(0, MAX_MATCHES).map(tagMatch),
			message: `${candidates.length} restricted tags are called “${text}”. Pick one:`,
		});
	}
	const other = tagsNamed(text, collectionTags);
	if (other.length) return notRestricted(other[0]);
	const mine = tagsNamed(text, privateTags);
	if (mine.length) return privateOne(mine[0]);
	const needle = fold(text);
	const titled = Object.values({ ...rememberedItemTagTitles(), ...(itemTitles || {}) }).find(entry => entry && fold(entry.title) === needle);
	if (titled) return itemOne(titled.title);
	const offered = closeMatches(restricted, needle, tag => [tag?.name, ...(Array.isArray(tag?.aliases) ? tag.aliases : []), tagPathLabel(tag)])
		.slice(0, MAX_MATCHES)
		.map(tagMatch);
	return verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, {
		matches: offered,
		message: offered.length ? `“${text}” is not a restricted tag here. Did you mean:` : `“${text}” is not a restricted tag here.`,
	});
}

registerIdValueKind('tag', {
	before: 'choice',
	appliesTo: field => field?.dataType === 'tag',
	check(text, { field, cmp }) {
		if (!IDENTITY_CMPS.has(cmp)) return null;
		const options = field?.options || {};
		// Nothing to judge by until the vocabulary has arrived.
		if (!options.loaded) return null;
		const collectionTags = Array.isArray(options.tags) ? options.tags : [];
		const privateTags = Array.isArray(options.privateTags) ? options.privateTags : [];
		if (options.mode === 'access') return checkAccessTag(text, { collectionTags, privateTags, itemTitles: options.itemTitles || null });
		const live = options.mode !== 'collection';
		const reachable = live ? [...collectionTags, ...privateTags] : collectionTags;

		if (looksLikeId(text)) {
			const id = text.toLowerCase();
			const mine = privateTags.find(tag => tagIdOf(tag) === id);
			if (mine && !live) {
				return verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, { message: `“${mine.name}” is your private tag. ${PRIVATE_HERE}` });
			}
			const known = reachable.find(tag => tagIdOf(tag) === id);
			if (known) return verdict('tag', ID_CHECK_STATUS.OK, text, { value: id, via: 'value', matches: [tagMatch(known)] });
			const item = itemTagTitleFor(id) || (options.itemTitles && options.itemTitles[id]);
			if (item?.title) return verdict('tag', ID_CHECK_STATUS.OK, text, { value: id, via: 'value', matches: [{ value: id, label: item.title }] });
			return verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, {
				message: 'This tag can’t be found. It may have been deleted, or you can’t see it.',
			});
		}

		// A name, an alias or a path ("Digestive/Stomach pain").
		const candidates = tagsNamed(text, reachable);
		if (candidates.length === 1) {
			return verdict('tag', ID_CHECK_STATUS.OK, text, {
				value: tagIdOf(candidates[0]),
				via: 'label',
				noun: 'tag',
				matches: [tagMatch(candidates[0])],
			});
		}
		if (candidates.length > 1) {
			return verdict('tag', ID_CHECK_STATUS.AMBIGUOUS, text, {
				matches: candidates.slice(0, MAX_MATCHES).map(tagMatch),
				message: `${candidates.length} tags are called “${text}”. Pick one:`,
			});
		}
		if (!live) {
			const mine = tagsNamed(text, privateTags);
			if (mine.length) return verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, { message: `“${text}” is your private tag. ${PRIVATE_HERE}` });
		}
		// An item tag's title: item tags are picked (by id), never typed.
		const needle = fold(text);
		const known = { ...rememberedItemTagTitles(), ...(options.itemTitles || {}) };
		const items = Object.entries(known)
			.filter(([, entry]) => entry && fold(entry.title) === needle)
			.slice(0, MAX_MATCHES)
			.map(([id, entry]) => ({ value: id, label: entry.title, detail: 'Item tag' }));
		if (items.length) {
			return verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, {
				matches: items,
				message: `“${text}” names no tag here: an item used as a tag is picked, not typed. Pick it:`,
			});
		}
		const offered = closeMatches(reachable, needle, tag => [tag?.name, ...(Array.isArray(tag?.aliases) ? tag.aliases : []), tagPathLabel(tag)])
			.slice(0, MAX_MATCHES)
			.map(tagMatch);
		return verdict('tag', ID_CHECK_STATUS.UNKNOWN, text, {
			matches: offered,
			message: offered.length ? `“${text}” is not a tag here. Did you mean:` : `“${text}” is not a tag here.`,
		});
	},
});

// ------------------------------------------------------------ rows and nodes

// The typed text behind one value node, or undefined when the node is not a
// literal somebody could have typed (a reference, `me`, a date, a relative).
const typedTextOf = node => {
	if (!node) return undefined;
	if (node.type === 'str' || node.type === 'num') return node.v;
	return undefined;
};

/**
 * Every id-backed value in one condition, checked: `[{ member, typed,
 * result }]`, `member` being the position in an `in (…)` list (null for a
 * single value). Values with nothing to say are left out.
 */
export function checkConditionValues(field, cmp, value, context = {}) {
	if (!value) return [];
	const members = value.type === 'list' ? (value.v || []) : [value];
	const out = [];
	members.forEach((node, index) => {
		const typed = typedTextOf(node);
		if (typed === undefined) return;
		const result = checkIdValue(field, typed, { ...context, cmp });
		if (result) out.push({ member: value.type === 'list' ? index : null, typed: String(typed), result });
	});
	return out;
}

// DFS over a parsed query's conditions, in a stable order (the order a
// location's `node` index counts in). A repeating group's block (KPR-683) is
// one condition on the group and then its inside, depth-first, each read in the
// group's COLUMNS: `visit(node, index, columns)`, `columns` the vocabulary a
// condition inside a block is read in (null at the top). `columnsOf(node)`
// says what a block's columns are.
function walkConditions(ast, visit, columnsOf = null) {
	let index = 0;
	const walk = (node, columns) => {
		if (!node) return;
		if (node.op === 'cmp') {
			visit(node, index++, columns);
			if (!columns && isBlock(node) && typeof columnsOf === 'function') {
				const inside = columnsOf(node);
				if (inside) walk(node.value.v, inside);
			}
			return;
		}
		(node.args || []).forEach(child => walk(child, columns));
		if (node.arg) walk(node.arg, columns);
	};
	walk(ast, null);
}

/** The columns a block's group offers, for `walkConditions`: `resolve` is the caller's field lookup. */
const blockColumnsVia = resolve => (node) => {
	const group = resolve(node.path || [], !!node.forceElement, false);
	return isEntriesField(group) ? entryColumnFields(group) : [];
};

/**
 * Every id-backed value in a parsed query, checked — what a text mode (the
 * filter builder's Advanced tab, the condition builder's "Edit as text")
 * shows under the text. `vocabularies` is keyed by kind name
 * (`{ user: [...], lookup: {...} }`); `find(path, forceElement, facet)`
 * resolves a condition's field (the builder's own `findField`).
 *
 * Returns `[{ at: { node, member }, field, typed, result }]`, `node` counting
 * conditions depth-first.
 */
export function checkQueryValues(ast, fields, { vocabularies = {}, find } = {}) {
	const resolve = typeof find === 'function'
		? find
		: (path, forceElement, facet) => findField(fields, path, forceElement, facet);
	const out = [];
	walkConditions(ast, (node, index, columns) => {
		if (isNullaryOperator(node.cmp) || isBlock(node)) return;
		const field = columns ? findField(columns, node.path || [], !!node.forceElement, false) : resolve(node.path || [], !!node.forceElement, !!node.facet);
		const name = idValueKindFor(field);
		if (!name) return;
		checkConditionValues(field, node.cmp, node.value, { vocabulary: vocabularies[name] })
			.forEach(({ member, typed, result }) => out.push({ at: { node: index, member }, field, typed, result }));
	}, blockColumnsVia(resolve));
	return out;
}

/**
 * A condition whose OPERATOR the API refuses (utils/kqlBuilder.js
 * refusedOperatorMessage — `tags ~ x`, `tags > x`, emptiness in an access
 * query; T7, ruled b26), as a check IdValueCheck draws like a flag: one
 * line, nothing to pick. Null when the operator is fine. Unlike a value's
 * check this is not advisory — the API answers 400 — so the filter builder
 * also holds Apply while one stands.
 */
export function checkOperator(field, cmp) {
	const message = refusedOperatorMessage(field, cmp);
	if (!message) return null;
	return { member: null, typed: '', result: { kind: 'operator', status: ID_CHECK_STATUS.UNKNOWN, typed: '', matches: [], message } };
}

/**
 * Every refused operator in a parsed query (see `checkOperator`), wherever
 * it sits: `[{ at: { node, member: null }, field, typed, result }]`, in the
 * text modes' shape.
 */
export function checkQueryOperators(ast, fields, { find } = {}) {
	const resolve = typeof find === 'function'
		? find
		: (path, forceElement, facet) => findField(fields, path, forceElement, facet);
	const out = [];
	walkConditions(ast, (node, index, columns) => {
		if (isBlock(node)) return;
		const field = columns ? findField(columns, node.path || [], !!node.forceElement, false) : resolve(node.path || [], !!node.forceElement, !!node.facet);
		const check = checkOperator(field, node.cmp);
		if (check) out.push({ ...check, at: { node: index, member: null }, field });
	}, blockColumnsVia(resolve));
	return out;
}

/**
 * The query with ONE value replaced — what a one-click match does in a text
 * mode. `at` is a location from `checkQueryValues`; `next` the stored value
 * to write (always a string: a choice value, an item or user id). Pure: the
 * input AST is not mutated.
 */
export function replaceQueryValue(ast, at, next) {
	if (!ast || !at) return ast;
	const replacement = { type: 'str', v: String(next) };
	let index = 0;
	const rebuild = node => {
		if (!node) return node;
		if (node.op === 'cmp') {
			const mine = index++ === at.node;
			// A repeating group's block: its inside counts on, depth-first (walkConditions).
			if (isBlock(node)) return { ...node, value: { ...node.value, v: rebuild(node.value.v) } };
			if (!mine || !node.value) return node;
			if (node.value.type === 'list') {
				if (at.member === null || at.member === undefined) return node;
				return { ...node, value: { ...node.value, v: node.value.v.map((member, i) => (i === at.member ? replacement : member)) } };
			}
			return { ...node, value: replacement };
		}
		if (Array.isArray(node.args)) return { ...node, args: node.args.map(rebuild) };
		if (node.arg) return { ...node, arg: rebuild(node.arg) };
		return node;
	};
	return rebuild(ast);
}

/**
 * The query with every LABEL replaced by the stored value it stands for —
 * `status = "In progress"` becomes `status = in-progress`, which is what the
 * API compares. Only unambiguous labels move; everything else (an unknown, an
 * ambiguous name, a title match, an id the API reads anyway) stays exactly
 * as written. `{ ast, changed }`.
 */
export function resolveQueryLabels(ast, fields, options = {}) {
	if (!ast) return { ast, changed: false };
	// A facet named by its card's key (written before F3) is renamed to the
	// facet's id first: `@people = x` → `@<card id> = x`. A field whose
	// vocabulary keeps its keys (`keepKeys`: text the API resolves itself —
	// who-sees-what text, a lookup's picker filter, a notification's trigger)
	// moves nothing: its key READS as its record and stays as written (the
	// F3 reviews' F1, F6, N2), while a choice's label still becomes its value.
	const renamed = normalizeFacetAliases(ast, fields);
	const moves = checkQueryValues(renamed.ast, fields, options)
		.filter(({ field, result }) => result.status === ID_CHECK_STATUS.OK && result.via === 'label' && result.value !== undefined
			&& !field?.keepKeys);
	if (!moves.length) return renamed;
	const next = moves.reduce((current, { at, result }) => replaceQueryValue(current, at, result.value), renamed.ast);
	return { ast: next, changed: true };
}

/**
 * Query text with its labels resolved (`resolveQueryLabels`), as canonical
 * text. Text that does not parse, or has nothing to resolve, comes back as
 * given.
 */
export function resolveQueryTextLabels(text, fields, options = {}) {
	const trimmed = (text || '').trim();
	if (!trimmed) return text;
	let ast;
	try {
		ast = parse(trimmed);
	} catch (err) {
		return text;
	}
	const { ast: next, changed } = resolveQueryLabels(ast, fields, options);
	return changed ? serialize(next) : text;
}
